import { bin, circleTick } from "@simplybusiness/icons";
import {
  Button,
  Divider,
  Flex,
  Icon,
  Text,
  useBreakpoint,
} from "@simplybusiness/mobius";
import { Fragment, useContext, useState } from "react";
import { CombinedPaymentContext } from "../CombinedPaymentContext";
import { fetchRequest } from "../UtilsHelper";
import { useTriggerEventTracker } from "../helpers/PersistentPaymentSummaryHelper";
import { formatAmount } from "../../../QuoteCards/QuoteCard/Coverage/transformCoverage";

function RenderItem(label: string, value: string, isPrimary: boolean) {
  return (
    <div className="mobius-payment-summary__styled-grid">
      <Text
        elementType="span"
        className={
          isPrimary
            ? "mobius-payment-summary__styled-grid-primary-text"
            : "mobius-payment-summary__styled-grid-secondary-text"
        }
      >
        {label}
      </Text>
      <Text
        elementType="span"
        className={
          "align-right " +
          (isPrimary
            ? "mobius-payment-summary__styled-grid-primary-text"
            : "mobius-payment-summary__styled-grid-secondary-text")
        }
      >
        {value}
      </Text>
    </div>
  );
}

/** Extract cover information inside given product */
function SummaryFragment(
  product: Record<string, ProductProps>,
  mobileView: boolean,
  policyStartDate?: string,
  totalGrossPremium?: string,
  hasSharedLimits?: boolean,
  applicationDashboard?: boolean,
  hasPrimaryCover: boolean = false,
) {
  const productCovers = Object.keys(product);
  const covers = ["Contractors' Equipment & Small Tools"];
  const breakpointSize = useBreakpoint([
    { size: "sm", value: 321 },
    { size: "md", value: 322 },
    { size: "lg", value: 641 },
  ]).breakpoint.size;

  const isMobile: boolean = breakpointSize == "md" || mobileView;

  const minMobileCovers = isMobile && productCovers.length > 1;
  const minDesktopCovers = !isMobile && productCovers.length > 0;
  const isCombinedPaymentPage = useContext(CombinedPaymentContext);

  function showCover(cover: string) {
    const show = product[cover].displayPremium !== "";
    return show;
  }

  function showPolicyStartDate(idx: number) {
    const show = !applicationDashboard && policyStartDate;
    if (hasPrimaryCover) {
      return show && idx === 0 && hasSharedLimits;
    }
    return show;
  }

  function showPrimaryCoverDivider(idx: number) {
    return hasPrimaryCover && idx === 0 && hasSharedLimits;
  }

  function showSecondaryCoverDivider(idx: number) {
    return hasPrimaryCover && idx !== 0 && hasSharedLimits;
  }

  function showNonPrimaryCoverDivider(cover: string) {
    return (
      !hasPrimaryCover &&
      product[cover].displayPremium !== "" &&
      policyStartDate
    );
  }

  return (
    <div
      className={`mobius-payment-summary__summary-fragment-wrapper ${isCombinedPaymentPage ? "combined-styles" : undefined}`}
    >
      {productCovers.map((cover, idx) => (
        <Fragment key={cover}>
          {showCover(cover) && (
            <>
              <Flex className="mobius-payment-summary__cover">
                {totalGrossPremium &&
                (!hasSharedLimits || (idx === 0 && hasSharedLimits))
                  ? RenderItem(cover, totalGrossPremium, true)
                  : RenderItem(cover, "", true)}
              </Flex>
              {showPolicyStartDate(idx) && (
                <>
                  <Flex
                    className={
                      !isMobile
                        ? "mobius-payment-summary__flex-margin-bottom"
                        : ""
                    }
                  >
                    {RenderItem(
                      `Policy starting ${policyStartDate}`,
                      "",
                      false,
                    )}
                  </Flex>
                  {showPrimaryCoverDivider(idx) && <Divider />}
                </>
              )}
            </>
          )}
          {showNonPrimaryCoverDivider(cover) && <Divider />}
          {product[cover].displayLimits &&
            product[cover].displayLimits.map(
              (limit: { label: string; value: string }) => {
                return limit.label ? (
                  <Flex
                    className="mobius-payment-summary__flex-margin-bottom"
                    key={limit.label}
                  >
                    {RenderItem(limit.label, limit.value, false)}
                  </Flex>
                ) : null;
              },
            )}
          {product[cover].displayExcessTitle &&
            product[cover].displayExcessAmount && (
              <Flex className="mobius-payment-summary__flex-margin-bottom">
                {RenderItem(
                  product[cover].displayExcessTitle,
                  formatAmount(product[cover].displayExcessAmount),
                  false,
                )}
              </Flex>
            )}
          {product[cover].displaySpecificExcesses && (
            <>
              {Object.keys(product[cover].displaySpecificExcesses).map(
                (excess: string) => (
                  <Flex
                    className="mobius-payment-summary__flex-margin-bottom"
                    key={excess}
                  >
                    <>
                      {RenderItem(
                        excess,
                        formatAmount(
                          product[cover].displaySpecificExcesses[excess],
                        ),
                        false,
                      )}
                    </>
                  </Flex>
                ),
              )}
              {showSecondaryCoverDivider(idx) && <Divider />}
            </>
          )}
        </Fragment>
      ))}
      {(minMobileCovers || minDesktopCovers) && (
        <Flex
          className={`mobius-payment-summary__minimum-covers ${
            applicationDashboard &&
            !productCovers.some(r => covers.indexOf(r) >= 0) &&
            !isMobile
              ? "mobius-payment-summary__minimum-covers-desktop"
              : ""
          }`}
        />
      )}
    </div>
  );
}

export function ProductSummary({
  productName,
  product,
  additionalCover,
  totalGrossPremium,
  mobileView,
  policyStartDate,
  hasSharedLimits,
  applicationDashboard = false,
  updateApplicationDashboard,
  isOnlyProduct,
  hasPrimaryCover,
}: {
  productName: string;
  product: Record<string, ProductProps>;
  additionalCover: Record<string, ProductProps>;
  totalGrossPremium?: string;
  mobileView: boolean;
  policyStartDate?: string;
  hasSharedLimits?: boolean;
  applicationDashboard?: boolean;
  updateApplicationDashboard?: (response: unknown) => void;
  isOnlyProduct?: boolean;
  hasPrimaryCover?: boolean;
}) {
  const [loading, setIsLoading] = useState(false);
  const additionalCoversKeys = Object.keys(additionalCover);
  const oneOrMoreProducts = Object.keys(product).length > 0;
  const [eventDetails, setEventDetails] = useState<{
    eventName: string | null;
    args: string[];
  }>({ eventName: null, args: [] });

  useTriggerEventTracker(eventDetails.eventName, ...eventDetails.args);

  const removeProduct = async () => {
    const url = `?step_action=remove_product`;
    const payload = JSON.stringify({ product_name: productName });
    const response: unknown = await fetchRequest(url, payload);
    if (response) {
      updateApplicationDashboard?.(response);
      setIsLoading(false);
    }
  };

  const handleRemoveButtonClick = (ev: React.MouseEvent<HTMLButtonElement>) => {
    ev.preventDefault();
    setIsLoading(true);
    setEventDetails({
      eventName: "dashboard_remove_product",
      args: [productName ? productName : ""],
    });
    void removeProduct();
  };

  const removeButton = () => {
    return isOnlyProduct ? (
      ""
    ) : (
      <Button
        size="sm"
        variant="ghost"
        isLoading={loading}
        isDisabled={loading}
        onClick={ev => {
          handleRemoveButtonClick(ev);
        }}
      >
        <div>
          <Icon icon={bin} title="Remove product" />
          {"\u00A0"}Remove
        </div>
      </Button>
    );
  };

  function checkPrimaryCover(
    hasPrimaryCover: boolean,
    product: Record<string, ProductProps>,
  ): Record<string, ProductProps> {
    if (hasPrimaryCover) {
      return {
        "Business Owner's Policy": {
          displayPremium: "0",
          displaySpecificExcesses: {},
          covered: true,
          optional: false,
          displayLimits: [{ label: "", value: "" }],
          displayExcessTitle: "",
          displayExcessAmount: "",
        },
        ...product,
      };
    }
    return product;
  }

  return (
    <>
      {oneOrMoreProducts &&
        SummaryFragment(
          checkPrimaryCover(hasPrimaryCover ? hasPrimaryCover : false, product),
          mobileView,
          policyStartDate,
          totalGrossPremium,
          hasSharedLimits,
          applicationDashboard,
          hasPrimaryCover,
        )}
      {additionalCoversKeys.length > 0 &&
        additionalCoversKeys.map(newCover => {
          const newCoverRecord: Record<string, ProductProps> = {
            [newCover]: additionalCover[newCover],
          };
          return (
            <Fragment key={newCover}>
              {oneOrMoreProducts && (
                <Text
                  className="mobius-payment-summary__additional-covers"
                  elementType="p"
                >
                  {" "}
                </Text>
              )}
              {SummaryFragment(
                newCoverRecord,
                mobileView,
                oneOrMoreProducts ? "" : policyStartDate,
                oneOrMoreProducts ? "" : totalGrossPremium,
                hasSharedLimits,
                applicationDashboard,
                hasPrimaryCover,
              )}
            </Fragment>
          );
        })}
      {applicationDashboard && (
        <div className="mobius-payment-summary__button-div">
          <Flex
            justifyContent="space-between"
            className="mobius-payment-summary__flex-margin-top"
          >
            <Icon
              icon={circleTick}
              color="var(--color-primary)"
              title="Quote completed successfully"
            />
            <span className="mobius-payment-summary__big-button-span">
              Quote completed
            </span>
          </Flex>
          {removeButton()}
        </div>
      )}
    </>
  );
}

export interface ProductProps {
  displayLimits: { label: string; value: string }[];
  displayPremium: string;
  displayExcessTitle: string;
  displayExcessAmount: string;
  displaySpecificExcesses: Record<string, string>;
  covered: boolean;
  optional: boolean;
}

export interface ProductSummaryProps {
  product: Record<string, ProductProps>;
  additionalCover: Record<string, ProductProps>;
  productName: string;
  mobileView: boolean;
  policyStartDate?: string;
  totalGrossPremium?: string;
  hasSharedLimits?: boolean;
  applicationDashboard?: boolean;
  updateApplicationDashboard: (response: unknown) => void;
  isOnlyProduct?: boolean;
  hasPrimaryCover?: boolean;
}
